/**
 * WCC360 v2 — "Your role in the problem" (X-CAUSE-ROLE-001).
 *
 * For each of the learner's top two ranked causes: which way would you most
 * want to contribute? Eight options, one per archetype, none of them the
 * right answer. A small capped nudge (see `causeCatalogue.ts`), applied
 * idempotently so revisiting the screen never double-counts.
 */
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { HowToAnswer } from "../components/interactions/HowToAnswer";
import { CAUSE_ROLES, MAX_ROLE_CAUSES, causeRoleDeltas } from "../causeCatalogue";
import { causeById } from "../causes";
import { addSignal, emptyVector, type ArchetypeVector } from "../archetypes";
import { useAssessmentV2 } from "../store";
import { cn } from "@/lib/utils";

interface Props { onNext: () => void }

export function CauseRoleScreen({ onNext }: Props) {
  const { state, setPart1, setPart2 } = useAssessmentV2();
  const causes = (state.part1.cause_rank_ids ?? []).slice(0, MAX_ROLE_CAUSES)
    .map((id) => causeById(id))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));
  const [roles, setRoles] = useState<Record<string, string>>(state.part1.cause_roles ?? {});

  const answered = causes.filter((c) => roles[c.id]).length;
  const canContinue = answered === causes.length;

  const commit = () => {
    const prev = causeRoleDeltas(state.part1.cause_roles);
    const kept: Record<string, string> = {};
    for (const c of causes) if (roles[c.id]) kept[c.id] = roles[c.id];
    let tally = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
    tally = addSignal(tally, prev, -1);
    tally = addSignal(tally, causeRoleDeltas(kept), 1);
    setPart2("archetype_tally", tally);
    setPart1({ cause_roles: kept });
    onNext();
  };

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h2 className="font-display text-2xl md:text-3xl font-medium leading-snug">
        Your role in the problem
      </h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">
        There are many ways to make something better. None of these is the right answer. Pick the one you'd most enjoy.
      </p>
      <div className="mt-4">
        <HowToAnswer>Choose one way of helping for each cause. Tap another option to change your answer.</HowToAnswer>
      </div>

      {causes.map((c, ci) => (
        <fieldset key={c.id} className="mt-8">
          <legend className="font-display text-lg font-medium">
            If you worked on <span className="text-primary">{c.label.toLowerCase()}</span>, which would you most want to do?
          </legend>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {CAUSE_ROLES.map((r) => {
              const on = roles[c.id] === r.id;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() => setRoles((p) => ({ ...p, [c.id]: r.id }))}
                    className={cn(
                      "w-full min-h-[52px] rounded-2xl border px-4 py-3 text-left text-sm font-medium",
                      "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      on ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40",
                    )}
                  >
                    {r.label}
                    <span className="sr-only"> for cause {ci + 1}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}

      <div className="mt-8">
        <button
          onClick={commit}
          disabled={!canContinue}
          className="min-h-[52px] w-full sm:w-auto px-7 rounded-2xl bg-primary text-primary-foreground
            font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        {!canContinue && (
          <p className="mt-3 text-xs text-muted-foreground" aria-live="polite">
            Choose one option for each cause to continue.
          </p>
        )}
      </div>
    </div>
  );
}
